import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { cn } from '@plato/ui/util/classes';
import type { ReactNode } from 'react';
import type { GetManyEventsListItem } from '../../../server/types/infer-trpc-types.ts';
import { isEventOccurrenceLocked } from '../../../utils/event-lock-utils.ts';
import { getSortableEventId } from '../utils/get-sortable-event-id.ts';
import { EventCard } from './event-card.tsx';

type Props = {
    event: GetManyEventsListItem;
    date: string;
};

export function SortableEventCard({ event, date }: Props): ReactNode {
    const { userId } = useAppConfig();
    const isOwner = event.owner_id === userId;

    // STEER-121: an occurrence with a note on the same or a later day is locked — the
    // range-bounded `event.notes` can't see later out-of-range notes, so use latestNoteDate.
    const canDrag =
        isOwner &&
        !isEventOccurrenceLocked({
            isRecurring: !!event.recurrenceRule,
            latestNoteDate: event.latestNoteDate,
            occurrenceDate: event.date,
        });

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: getSortableEventId(event),
        disabled: !canDrag,
    });

    const style = {
        transform: CSS.Translate.toString(transform),
        transition,
    };

    const drag = canDrag ? listeners : undefined;

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...drag} data-testid="event-card">
            <EventCard
                className={cn({
                    'opacity-0': isDragging,
                })}
                event={event}
                date={date}
            />
        </div>
    );
}
